import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import SectionHeader from '../CoreComponents/Details/SectionHeader';
import { setModalOpen } from '../../store/Utilities';
import { FieldRenderer } from './FieldRenderer';
import { useAddChecklistTaskSubmit } from './useAddChecklistTaskSubmit';
import { getVariant } from './variants';
import { DDCheckList } from '../../types/DataDD/DataDD';

type TProps = {
    firstTask: DDCheckList;
    path: string;
};

export function AddChecklistTaskForm({ firstTask, path }: TProps) {
    const dispatch = useDispatch();
    const variant = useMemo(() => getVariant(firstTask?.drilldownTypeId), [firstTask?.drilldownTypeId]);

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm({
        resolver: zodResolver(variant.schema),
        defaultValues: variant.defaultValues,
    });

    const onSubmit = useAddChecklistTaskSubmit(variant, firstTask, path, reset);

    const handleCloseModal = () => {
        dispatch(setModalOpen(false));
        reset();
    };

    return (
        <div className="max-w-screen-md mt-4">
            <div className="mt-8 mb-8">
                <SectionHeader title="Add New Task" />
            </div>
            <form onSubmit={handleSubmit(onSubmit as any)}>
                {variant.fields.map((field) => (
                    <FieldRenderer
                        key={field.key}
                        field={field}
                        register={register}
                        control={control}
                        errors={errors}
                    />
                ))}
                <div className="flex justify-between mt-16">
                    <button
                        type="button"
                        data-testid="add-task-form-cancel-button"
                        onClick={handleCloseModal}
                        className="mt-3 text-gray-900 bg-gray-500 dark:text-gray-900 dark:bg-gray-500"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        data-testid="add-task-form-submit-button"
                        className="text-white bg-orange-1000 dark:text-gray-800 dark:bg-orange-1000"
                    >
                        Submit
                    </button>
                </div>
            </form>
        </div>
    );
}
